import * as React from 'react';
import cn from 'classnames';
import * as Buttons from '@library/Buttons';
import { toggleOption } from '@library/pill-group/options';
import HourSelect from '../../hours/HourSelect';
import Link from './Link';
import * as schedule from './schedule';

type Props = {
  known: boolean;
  onApply: (keys: string[], spans: string[]) => void;
};

function BulkHours(props: Props) {
  const [selected, setSelected] = React.useState<string[]>([]);
  const [span, setSpan] = React.useState(schedule.DEFAULT_TIMESPAN);
  const { open, close } = schedule.splitSpan(span);
  const noneSelected = selected.length === 0;

  const apply = (spans: string[]) => {
    props.onApply(selected, spans);
    setSelected([]);
  };

  return (
    <div
      className="editor__hours__bulk"
      data-cy="bulk-hours"
      role="group"
      aria-labelledby="bulk-hours-title"
    >
      <div className="editor__hours__bulk__header">
        <span className="editor__hours__bulk__title" id="bulk-hours-title">
          {'Set several days at once'}
        </span>
        <div className="editor__hours__day__actions">
          <Link cy="select-weekdays" onClick={() => setSelected(schedule.WEEKDAYS)}>
            {'Weekdays'}
          </Link>
          <Link cy="select-weekend" onClick={() => setSelected(schedule.WEEKEND)}>
            {'Weekend'}
          </Link>
          <Link cy="select-all" onClick={() => setSelected(schedule.ALL_DAYS)}>
            {'All'}
          </Link>
          <Link cy="select-none" onClick={() => setSelected([])}>
            {'Clear'}
          </Link>
        </div>
      </div>

      <div className="editor__hours__bulk__row">
        {schedule.DAYS.map((day) => (
          <button
            key={day.key}
            type="button"
            data-cy="bulk-day"
            aria-label={day.name}
            aria-pressed={selected.includes(day.key)}
            className={cn('editor__hours__bulk__day', {
              'editor__hours__bulk__day--selected': selected.includes(day.key)
            })}
            onClick={() => setSelected((keys) => toggleOption(keys, day.key))}
          >
            {day.name.slice(0, 3)}
          </button>
        ))}
      </div>

      <div className="editor__hours__bulk__row">
        <div className="editor__hours__day__span">
          <HourSelect
            label="Opening time for selected days"
            value={open}
            onChange={(v) => setSpan(schedule.joinSpan(v, close))}
          />
          <span className="editor__hours__day__dash">{'—'}</span>
          <HourSelect
            label="Closing time for selected days"
            value={close}
            onChange={(v) => setSpan(schedule.joinSpan(open, v))}
          />
        </div>
        <Buttons.ApplyButton
          cy="apply-hours"
          className="editor__hours__bulk__apply"
          disabled={noneSelected}
          onClick={() => apply([span])}
        />
        <span className="editor__hours__bulk__divider" aria-hidden="true" />
        <Buttons.WhiteButton
          cy="apply-open-24"
          disabled={noneSelected}
          onClick={() => apply([schedule.OPEN_24_HRS])}
        >
          {'Open 24 hours'}
        </Buttons.WhiteButton>
        <Buttons.WhiteButton
          cy="apply-closed"
          disabled={noneSelected || !props.known}
          onClick={() => apply([])}
        >
          {'Closed'}
        </Buttons.WhiteButton>
      </div>
    </div>
  );
}

export default BulkHours;
